import {
  PayloadAction,
  createSlice,
  createAsyncThunk,
  ThunkDispatch,
  UnknownAction,
} from '@reduxjs/toolkit';
import { ImageMetadata } from '../../types/ui';
import {
  LoadScript,
  SetAnswer,
  SetDecision,
  SetMediaLabel,
  UpdateMediaLabel,
  SetModifyAnswer,
  SetFollowup,
  SetAccept,
  SetKeepExisting,
  AddMediaLabelToImage,
  DeleteMediaLabelFromImage,
  MoveMedia,
  ClearAuditSaved,
  ClearAuditChanged,
} from '@safeguardproperties/corescript-react-npm';
import {
  CoreDynamics,
  DynamicsCache,
  DynamicsRequest,
} from '@safeguardproperties/corescript-react-npm/dist/types/dynamics';
import {
  AuditStateData,
  CoreScript,
  Media,
  SaveData,
  ScriptAnswer,
  ScriptData,
  StateType,
} from '@safeguardproperties/corescript-react-npm/dist/types';

import { DefaultDynamicsFunction } from '../dynamicsHelper';
import { SetDynamicsResult } from '@safeguardproperties/corescript-react-npm/dist/corescript/actions/set_dynamics_results';

import { RootState } from '../Store';
import dayjs from 'dayjs';
import { DateFormat } from '../../constants';
const sliceName = 'coreScriptReducer';
export interface CoreScriptState {
  coreScript: CoreScript;
  scriptTimestamp: string;
  contextId: string;
  additionalLabelList: AdditionalLabelModel[]; //List of labels to show with associating to a question
}

export const initState: CoreScriptState = {
  coreScript: {
    stateType: 'audit',
    dynamics: {
      cache: {} as { [key: string]: DynamicsCache },
      requests: {} as { [key: string]: DynamicsRequest },
    } as CoreDynamics,
  } as CoreScript,
  contextId: '',
  scriptTimestamp: '',
  additionalLabelList: [], //List of labels to show with associating to a question
};

// Payload Actions
export interface LoadScriptProps {
  scriptHash: string;
  scriptData: string;
  luggageData: string;
  medallionData: string | undefined;
  auditCurrentData: string | undefined;
  auditDecisionsData: string | undefined;
  seededData: string | undefined;
  decisionReasonsData: string | undefined;
  saveData: string | undefined;
  reset?: boolean;
  additionalLabelList: string;
}

export interface SetAnswerProps {
  questionPath: string;
  answers: string[];
}

export interface MediaLabelProps {
  label: string;
  thumbUri: string;
  guids: string[];
}
export interface AddMediaLabelProps {
  label: string;
  media: Media[];
}
export interface DeleteMediaLabelProps {
  label: string;
  guids: string[];
}

export interface SetDecisionProps {
  questionPath: string;
  decisionid: number;
  reasonid: number;
  comments: string;
}

export interface SetModifyAnswerProps {
  questionPath: string;
  answers: string[];
  comments: string | undefined;
}

export interface SetFollowupProps {
  questionPath: string;
  reasonid: number;
  comments: string;
}

export interface SetAcceptProps {
  questionPaths: string[];
}

export interface SetKeepExistingProps {
  questionPath: string;
  answers: string[];
}

export interface MoveMediaLabelProps {
  fromLabel: string;
  toLabel: string;
  guids: string[];
}

export interface AdditionalLabelModel {
  Key: string; //Photo or
  Value: AdditionalLabelValue[];
}

export interface AdditionalLabelValue {
  Label: string;
  Images: ImageMetadata[];
}
export interface SetAdditionalLabelProps {
  label: string;
  image: ImageMetadata;
}
export interface RemoveAdditionalLabelProps {
  label: string;
  guids?: string[]; //Optional guid to remove specific image from label
}
export function loadAdditionalLabelModel(
  input: string
): AdditionalLabelModel[] {
  const data = JSON.parse(input) as AdditionalLabelModel[];
  return data;
}
const apiCallFunction = async (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  getState: () => RootState
) => {
  // Drain the dynamics request queue. Each completed result may cascade and
  // queue chained requests (e.g. dependent dynamic questions); re-read the
  // map after every dispatch so we pick those up without re-firing the ones
  // we've already handled.
  const processed = new Set<string>();
  let pending = Object.entries(
    getState().coreScript.coreScript.dynamics.requests
  ).filter(([key]) => !processed.has(key));

  while (pending.length > 0) {
    for (const [key, request] of pending) {
      processed.add(key);
      console.log(`Key: ${key}, Request:`, request);
      const resp = {
        questionPath: request.questionPath,
        apiType: request.apiType,
        url: request.url,
        answers: [] as ScriptAnswer[],
      } as DynamicsCache;

      await DefaultDynamicsFunction(
        request.questionPath,
        request.url,
        request.apiType
      )
        .then((answers) => {
          resp.answers = answers;
        })
        .catch((error) => {
          console.log(error);
          resp.error = 'failed.to.get.dynamics';
        });

      dispatch(setDynamicsCacheReducer(resp));
    }

    pending = Object.entries(
      getState().coreScript.coreScript.dynamics.requests
    ).filter(([key]) => !processed.has(key));
  }
};

export const loadScript = createAsyncThunk(
  `${sliceName}/loadScript`,
  async (payload: LoadScriptProps, { dispatch, getState }) => {
    dispatch(loadScriptReducer(payload));
    await apiCallFunction(dispatch, getState as () => RootState);
  }
);

export const setAnswer = createAsyncThunk(
  `${sliceName}/setAnswer`,
  async (payload: SetAnswerProps, { dispatch, getState }) => {
    dispatch(setAnswerReducer(payload));
    await apiCallFunction(dispatch, getState as () => RootState);
  }
);

export const setModifyAnswer = createAsyncThunk(
  `${sliceName}/setModifyAnswer`,
  async (payload: SetModifyAnswerProps, { dispatch, getState }) => {
    dispatch(setModifyAnswerReducer(payload));
    await apiCallFunction(dispatch, getState as () => RootState);
  }
);
const coreScriptSlice = createSlice({
  name: 'coreScript',
  initialState: initState,
  reducers: {
    setDynamicsCacheReducer: (
      state: CoreScriptState,
      action: PayloadAction<DynamicsCache>
    ) => {
      SetDynamicsResult(state.coreScript, action.payload);
    },
    loadScriptReducer: (
      state: CoreScriptState,
      action: PayloadAction<LoadScriptProps>
    ) => {
      LoadScript(
        state.coreScript,
        action.payload.scriptHash,
        action.payload.scriptData,
        action.payload.luggageData,
        action.payload.saveData,
        action.payload.medallionData,
        action.payload.auditCurrentData,
        action.payload.auditDecisionsData,
        action.payload.seededData,
        undefined,
        action.payload.decisionReasonsData
      );

      const value = dayjs()?.format(DateFormat.DATE_TIME_YYYY_MM_DD_HH_MM_a);
      state.scriptTimestamp = value;
      if (action.payload.additionalLabelList) {
        state.additionalLabelList = loadAdditionalLabelModel(
          action.payload.additionalLabelList
        );
      }
    },
    clearAuditSaved: (state: CoreScriptState) => {
      ClearAuditSaved(state.coreScript);
    },
    clearAuditChanged: (state: CoreScriptState) => {
      ClearAuditChanged(state.coreScript);
    },
    setDecision: (
      state: CoreScriptState,
      action: PayloadAction<SetDecisionProps>
    ) => {
      SetDecision(
        state.coreScript,
        action.payload.questionPath,
        action.payload.decisionid,
        action.payload.reasonid,
        action.payload.comments
      );
    },
    setAnswerReducer: (
      state: CoreScriptState,
      action: PayloadAction<SetAnswerProps>
    ) => {
      SetAnswer(
        state.coreScript,
        action.payload.questionPath,
        action.payload.answers
      );
    },
    setModifyAnswerReducer: (
      state: CoreScriptState,
      action: PayloadAction<SetModifyAnswerProps>
    ) => {
      SetModifyAnswer(
        state.coreScript,
        action.payload.questionPath,
        action.payload.answers,
        action.payload.comments
      );
    },
    setFollowup: (
      state: CoreScriptState,
      action: PayloadAction<SetFollowupProps>
    ) => {
      SetFollowup(
        state.coreScript,
        action.payload.questionPath,
        action.payload.reasonid,
        action.payload.comments
      );
    },
    setAccept: (
      state: CoreScriptState,
      action: PayloadAction<SetAcceptProps>
    ) => {
      SetAccept(state.coreScript, action.payload.questionPaths);
    },
    setKeepExisting: (
      state: CoreScriptState,
      action: PayloadAction<SetKeepExistingProps>
    ) => {
      SetKeepExisting(
        state.coreScript,
        action.payload.questionPath,
        action.payload.answers
      );
    },
    setMediaLabel: (
      state: CoreScriptState,
      action: PayloadAction<MediaLabelProps>
    ) => {
      SetMediaLabel(
        state.coreScript,
        action.payload.label,
        action.payload.thumbUri,
        action.payload.guids
      );
    },
    updateMediaLabel: (
      state: CoreScriptState,
      action: PayloadAction<MediaLabelProps>
    ) => {
      UpdateMediaLabel(
        state.coreScript,
        action.payload.label,
        action.payload.guids
      );
    },
    addMediaLabelToImage: (
      state: CoreScriptState,
      action: PayloadAction<AddMediaLabelProps>
    ) => {
      const payload = action.payload;
      AddMediaLabelToImage(
        state.coreScript,
        payload.label,
        payload.media,
        true
      );
    },
    deleteMediaLabel: (
      state: CoreScriptState,
      action: PayloadAction<DeleteMediaLabelProps>
    ) => {
      const { label, guids } = action.payload;
      DeleteMediaLabelFromImage(state.coreScript, label, guids);
    },
    moveMediasFromLabel: (
      state: CoreScriptState,
      action: PayloadAction<MoveMediaLabelProps>
    ) => {
      const { fromLabel, toLabel, guids } = action.payload;
      MoveMedia(state.coreScript, fromLabel, toLabel, guids);
    },
    setStateType: (
      state: CoreScriptState,
      action: PayloadAction<StateType>
    ) => {
      state.coreScript.stateType = action.payload;
    },
    //   clearScript: (state: ScriptEngineState) => {
    //     state.ScriptHash = '';
    //     state.ScriptData = {} as ScriptData;
    //     state.Luggage = {} as Luggage;
    //     state.SavedData = {
    //       Answers: {},
    //     } as SaveData;
    //     state.MobileState = {} as MobileStateData;
    //     state.State = {} as WebStateData;
    //     state.Error = undefined;
    //   },
    setStarted: (state: CoreScriptState, action: PayloadAction<boolean>) => {
      state.coreScript.saveData.Started = action.payload;
    },

    setCoreScriptContextId: (
      state: CoreScriptState,
      action: PayloadAction<string>
    ) => {
      state.contextId = action.payload;
    },
    resetCoreScript: (state: CoreScriptState) => {
      state.coreScript = initState.coreScript;
      state.contextId = '';
    },
    setAdditionalLabel: (
      state: CoreScriptState,
      action: PayloadAction<SetAdditionalLabelProps>
    ) => {
      const { label, image } = action.payload;
      // Find the label group
      const labelGroup = state.additionalLabelList.find(
        (l) => l.Key === 'Photo'
      );
      if (labelGroup === undefined) {
        // If label group doesn't exist, add it
        state.additionalLabelList.push({
          Key: label,
          Value: [{ Label: label, Images: [image] }],
        });
      } else {
        // Find the label value
        const labelValue = labelGroup.Value.find((v) => v.Label === label);
        if (labelValue === undefined) {
          // If label value doesn't exist, add it

          labelGroup.Value.push({ Label: label, Images: [image] });
        } else {
          if (!labelValue.Images) {
            labelValue.Images = [];
          }
          const exists = labelValue.Images.some(
            (img) => img.guid === image.guid
          );
          if (!exists) {
            labelValue.Images.push(image);
          }
        }
      }
    },
    removeAdditionalLabel: (
      state: CoreScriptState,
      action: PayloadAction<RemoveAdditionalLabelProps>
    ) => {
      const { label, guids } = action.payload;
      // Find the label group
      const labelGroup = state.additionalLabelList.find(
        (l) => l.Key === 'Photo'
      );
      if (labelGroup === undefined) {
        // If label group doesn't exist, return
        return;
      }

      const labelValue = labelGroup.Value.find((v) => v.Label === label);
      if (labelValue) {
        if (guids && guids.length > 0) {
          // If Guid is provid  ed, remove specific image
          labelValue.Images = labelValue.Images.filter(
            (img) => !(guids ?? []).includes(img.guid)
          );
          return;
        }
        labelValue.Images = [];
      }
    },
  },
});

export const getScriptHash = (state: RootState): string =>
  state.coreScript.coreScript.saveData.ScriptHash;

export const getScriptState = (state: RootState): AuditStateData =>
  state.coreScript.coreScript.state as AuditStateData;

export const getScriptSave = (state: RootState): SaveData =>
  state.coreScript.coreScript.saveData;

export const getScriptData = (state: RootState): ScriptData =>
  state.coreScript.coreScript.scriptData;

//List of labels for updating image descriptions that have no association to the script
export const getAdditionalPhotoLabels = (
  state: RootState
): AdditionalLabelValue[] =>
  state.coreScript.additionalLabelList.find((item) => item.Key === 'Photo')
    ?.Value || [];

export const {
  loadScriptReducer,
  setAnswerReducer,
  clearAuditChanged,
  clearAuditSaved,
  setDecision,
  setModifyAnswerReducer,
  setFollowup,
  setAccept,
  setKeepExisting,
  setMediaLabel,
  updateMediaLabel,
  deleteMediaLabel,
  setStarted,
  setStateType,
  addMediaLabelToImage,
  moveMediasFromLabel,
  setCoreScriptContextId,
  resetCoreScript,
  setAdditionalLabel,
  removeAdditionalLabel,
  setDynamicsCacheReducer,
} = coreScriptSlice.actions;
export default coreScriptSlice.reducer;
